"use client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import useSWR from "swr";
import { getCustomer } from "@/server/user";
import { deleteToken } from "@/app/actions";
import { usePathname } from "next/navigation";
import { match } from "ts-pattern";
import { atom, useSetAtom } from "jotai";

interface UserInfo {
  id: string;
  front_user_email: string;
  email: string;
  first_name: string;
  role: {
    front_permissions: {
      action: string;
    }[];
    id: string;
    name: string;
    level: number;
  };
  phone_number: string;
  phone_area: string;
  organization: string;
  position: string;
  location: string;
  city: string;
}

const UserContext = createContext<{
  user?: UserInfo;
  isLoading?: boolean;
  clearToken?: () => void;
  token?: string;
}>({});

export const useUser = () => {
  return useContext(UserContext);
};

// export const useNeedLogin = (needLogin?: boolean) => {
//   const { user, isLoading } = useUser();
//   const router = useRouter();
//   const pathname = usePathname();

//   useEffect(() => {
//     if (!isLoading && !user && needLogin) {
//       router.replace(`/login?redirect_url=${encodeURIComponent(pathname)}`)
//     }
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [isLoading, user, needLogin]);
// }

async function handleUserService(
  token?: string
): Promise<UserInfo | undefined> {
  if (!token) {
    return undefined;
  }

  const res = await getCustomer(token);

  if (!res || "errors" in res) {
    deleteToken();
    return undefined;
  }

  if ("data" in res && Array.isArray(res.data)) {
    deleteToken();
    return undefined;
  }

  const level = match((res.data as UserInfo).role)
    .with({ name: "普通客户" }, () => 1)
    .with({ name: "大客户" }, () => 2)
    .with({ name: "经销商" }, () => 3)
    // .with({ name: "Front_Level_4" }, () => 4)
    // .with({ name: "Front_Level_5" }, () => 5)
    .otherwise(() => null);

  return {
    ...res.data,
    role: {
      ...res.data.role,
      level,
    },
  };
}

export default function LocaleToStorage(props: {
  token?: string;
  children?: React.ReactNode;
}) {
  const pathname = usePathname();

  const [token, setToken] = useState(props.token);

  const { data, isLoading } = useSWR(
    token && props.token ? ["/api/me", pathname] : null,
    async () => {
      if (!token) {
        return;
      }

      return handleUserService(token);
    },
    {
      onErrorRetry(err, key, config, revalidate, revalidateOpts) {
        if (err.status === 404) return;

        if (revalidateOpts.retryCount >= 2) return;
        setTimeout(() => revalidate(revalidateOpts), 5000);
      },
      onError(err, key, config) {
        deleteToken();
        setToken(undefined);
        return;
      },
    }
  );

  const clearToken = useCallback(async () => {
    setToken(undefined);

    await deleteToken();
  }, []);

  useEffect(() => {
    if (token) {
      window.localStorage.setItem("token", token);
    } else {
      window.localStorage.removeItem("token");
    }
  }, [token]);

  return (
    <UserContext.Provider
      value={{
        user: token ? data : undefined,
        isLoading,
        clearToken,
        token,
      }}>
      {props.children}
    </UserContext.Provider>
  );
}
